import { EditOutlined, ZoomInOutlined } from "@ant-design/icons";
import { Button, Row, Space, Tag, Tooltip, Col, Modal } from "antd";
import {
  camelToWords,
  formatDate,
  snakeToCamelCase,
  camelToSnakeCase,
  formatCurrency,
} from "common/utils";
import { PageHeader } from "components/pageHeader";
import { CursorTable } from "components/cursorTable";
import {
  useFetchDisputesQuery,
  useLazyFetchDisputePreviewQuery,
} from "features/disputeManagementTable/disputeTableAPI";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchDisputeCategoriesQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import ManageDisputeModal from "features/debtorAccountsTable/components/manageDisputeModal";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import moment from "moment-timezone";
import { useAppearance } from "providers/appearanceProvider";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import DisputeManagementTableFilters, {
  SIDER_ID,
} from "features/disputeManagementTable/components/disputeManagementTableFilters";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { DATE_TIME_FORMAT } from "common/constants";

const StyledHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

const StyledZoomInOutlined = styled(ZoomInOutlined)`
  cursor: pointer;
  color: #000;
`;

const StyledTable = styled(CursorTable)`
  & .ant-checkbox-input {
    pointer-events: auto;
  }

  & .ant-table-selection-column {
    pointer-events: none;
  }

  & .ant-table-cell,
  & .ant-table-header-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & .ant-table-container {
    height: calc(100vh - 230px);
    overflow: scroll;
  }
`;

const StyledCol = styled(Col)`
  overflow: auto;
`;

const INITIAL_PAGE_SIZE = 100;
const TABLE_ID = "disputeManagementTable";
const INITIAL_ORDER_BY = null;

export default function DisputeManagementTable() {
  const { data: constants } = useFetchBackendConstantsQuery();

  // dispute table querying
  const [filters, setFilters] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [virtualPage, setVirtualPage] = useState(1);
  const [prevToken, setPrevToken] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const { isLoading: isAppearanceLoading, appearance } = useAppearance();
  const getOrderBy = useMemo(() => {
    // If orderBy is in the search params, use it first
    if (searchParams.get("orderBy")) {
      return searchParams.get("orderBy");
    }
    // If appearance is loaded and has a defaultSortOrder, use it second
    if (
      appearance &&
      appearance.items?.[TABLE_ID]?.defaultSortOrder?.direction &&
      appearance.items?.[TABLE_ID]?.defaultSortOrder?.column
    ) {
      return `${
        appearance.items?.[TABLE_ID]?.defaultSortOrder?.direction === "ascend" ? "" : "-"
      }${camelToSnakeCase(appearance.items?.[TABLE_ID]?.defaultSortOrder?.column)}`;
    }
    // If all else fails, use the default
    return INITIAL_ORDER_BY;
  }, [appearance, searchParams]);

  const getPageSize = useMemo(() => {
    // If pageSize is in the search params, use it first
    if (searchParams.get("pageSize")) {
      return searchParams.get("pageSize");
    }
    // If appearance is loaded and has a pageSize, use it second
    if (appearance && appearance.items?.[TABLE_ID]?.pageSize) {
      return appearance.items?.[TABLE_ID]?.pageSize;
    }

    // If no page size in search params, use the default
    return INITIAL_PAGE_SIZE;
  }, [appearance, searchParams]);

  const handleSetVirtualPage = (newVirtualPage) => {
    setVirtualPage(newVirtualPage);
  };

  const {
    data: disputes,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useFetchDisputesQuery(
    {
      filters,
      nextToken,
      orderBy: getOrderBy,
      pageSize: getPageSize,
      prevToken,
    },
    {
      skip: isAppearanceLoading,
    },
  );
  const { data: isDisputeUpdateAuthorized } = useAuthorization(PERMISSIONS.DISPUTE__UPDATE);

  const getSortDirection = (key) => {
    const snakeCaseKey = camelToSnakeCase(key);
    if (getOrderBy === snakeCaseKey) {
      return "ascend";
    }
    if (getOrderBy === `-${snakeCaseKey}`) {
      return "descend";
    }
    return null;
  };

  const setParams = (pageSize, orderBy) => {
    const params = {};
    if (pageSize && pageSize !== "null") {
      params.pageSize = pageSize;
    }

    if (orderBy && orderBy !== "null") {
      params.orderBy = orderBy;
    }
    // @ts-ignore
    setSearchParams(params);
  };

  const handleTableChange = (newPagination, _, sorter) => {
    if (newPagination && Object.keys(newPagination).length > 0) {
      setVirtualPage(newPagination.virtualPage);
      setPrevToken(newPagination.prevToken);
      setNextToken(newPagination.nextToken);
      // This will reset orderBy and pageSize in useMemo
      if (newPagination.pageSize) {
        setParams(newPagination.pageSize, searchParams.get("orderBy"));
      }

      if (newPagination.orderBy) {
        setParams(searchParams.get("pageSize"), newPagination.orderBy);
      }
    }
    if (sorter) {
      const newOrderBy = `${sorter.order === "descend" ? "-" : ""}${camelToSnakeCase(
        sorter.field,
      )}`;
      setVirtualPage(1);
      setPrevToken(null);
      setNextToken(null);
      // This will reset orderBy in useMemo
      setParams(searchParams.get("pageSize"), newOrderBy);
    }
  };

  // table enrichers
  const { data: creditors } = useFetchCreditorSummariesQuery();
  const { data: categories } = useFetchDisputeCategoriesQuery();
  // modal management
  const [isDisputeModalVisible, setIsDisputeModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [fetchDisputePreview] = useLazyFetchDisputePreviewQuery({ id: activeRecord?.id });

  const onEditDispute = (record) => {
    setIsDisputeModalVisible(true);
    setActiveRecord(record);
  };

  const columns = [
    {
      title: "Dispute ID",
      width: 120,
      dataIndex: "id",
      key: "id",
      render: (text, record) => record.id ?? "-",
      sorter: (a, b) => a.id > b.id,
      sortOrder: getSortDirection("id"),
    },
    {
      title: "Dispute External ID",
      render: (_, record) => record.externalId || "-",
      width: 250,
    },
    {
      title: "Date Received",
      width: 120,
      dataIndex: "receivedDate",
      key: "receivedDate",
      render: (text, record) => (record.receivedDate ? formatDate(record.receivedDate) : "-"),
      sorter: (a, b) => moment(a.receivedDate).unix() - moment(b.receivedDate).unix(),
      sortOrder: getSortDirection("receivedDate"),
    },
    {
      title: "Account ID",
      width: 120,
      dataIndex: "accountExternalId",
      key: "accountExternalId",
    },
    {
      title: "Debtor Name",
      dataIndex: "debtorName",
      width: 260,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            const newWindow = window.open(`/debtors/${record.debtorId}`, "_blank");
            if (newWindow) newWindow.opener = null;
          }}
        >
          {record.debtorName}
        </Button>
      ),
    },
    {
      title: "Category",
      width: 120,
      render: (text, record) => {
        const tags = categories?.filter((category) => record.categoryTagIds.includes(category.id));
        return tags ? tags.map((tag) => <Tag key={tag.id}>{tag.name}</Tag>) : "-";
      },
    },
    {
      title: "Amount",
      width: 120,
      render: (text, record) => formatCurrency(record.amount) ?? "-",
    },
    {
      title: "Time Since Dispute",
      width: 150,
      dataIndex: "receivedDate",
      key: "timeSinceDispute",
      render: (text, record) =>
        record.receivedDate ? `${moment().diff(moment(record.receivedDate), "days")} days` : "-",
      sorter: (a, b) => moment(b.receivedDate).unix() - moment(a.receivedDate).unix(),
      sortOrder: getSortDirection("receivedDate"),
    },
    {
      title: "Status",
      render: (text, record) =>
        record.status ? camelToWords(snakeToCamelCase(record.status)) : "-",
    },
    {
      title: "Bureau Compliance Condition Code",
      render: (_, record) => record.accountComplianceConditionCode || "-",
      width: 300,
    },
    {
      title: "Channel Source",
      render: (text, record) =>
        record.channelSource ? camelToWords(snakeToCamelCase(record.channelSource)) : "-",
      width: 150,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (text, record) =>
        creditors?.find((each) => each.id === record.accountCreditorId)?.name,
    },
    {
      title: "Data Source",
      render: (_, record) =>
        record.dataSource ? camelToWords(snakeToCamelCase(record.dataSource)) : "-",
      width: 150,
    },
    {
      title: "E-Oscar Status Code",
      render: (_, record) =>
        record.eOscarStatusCode
          ? constants?.eOscarDisputeStatusCodes?.find(
              (eOscarStatusCode) => eOscarStatusCode.value === record.eOscarStatusCode,
            )?.display
          : "-",
      width: 250,
    },
    {
      title: "E-Oscar Status Updated At",
      render: (_, record) =>
        record.eOscarStatusCodeUpdatedAt
          ? moment(record.eOscarStatusCodeUpdatedAt).format(DATE_TIME_FORMAT)
          : "-",
      width: 220,
    },
    {
      title: "Evidence of Debt",
      dataIndex: "evidenceOfDebt",
      key: "evidenceOfDebt",
      render: (text, record) => (record.documents?.length > 0 ? "Yes" : "No"),
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      hidden: !isDisputeUpdateAuthorized,
      width: 100,
      render: (text, record) => (
        <Space size="middle">
          <Tooltip placement="bottom" title="Edit" key="edit">
            <EditOutlined key="edit" onClick={() => onEditDispute(record)} />
          </Tooltip>
          {record?.dataSource === "e_oscar" && (
            <Tooltip placement="bottom" title="Preview Dispute" key="preview">
              <Button
                type="link"
                title="View Data"
                icon={<StyledZoomInOutlined />}
                onClick={async () => {
                  const result = await fetchDisputePreview({ id: record.id });
                  if ("data" in result) {
                    Modal.info({
                      title: "Preview",
                      width: "60vw",
                      okText: "Close",
                      onOk: () => {
                        Modal.destroyAll();
                      },
                      content: result.data?.htmlContent ? (
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: result.data?.htmlContent }}
                        />
                      ) : (
                        <div>No preview available</div>
                      ),
                    });
                  }
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
      fixed: "right",
    },
  ].filter((column) => !column.hidden);

  const onManageDisputeModalCancel = () => {
    setIsDisputeModalVisible(false);
    setActiveRecord(null);
  };

  return (
    <>
      <StyledHeader>Disputes</StyledHeader>
      <Row wrap={false}>
        {!appearance?.items?.[SIDER_ID]?.hidePanel && (
          <DisputeManagementTableFilters filters={filters} setFilters={setFilters} />
        )}
        <StyledCol>
          <StyledTable
            sticky
            bordered
            loading={isTableLoading || isTableFetching || isAppearanceLoading}
            scroll={{ x: "max-content" }}
            pageSize={getPageSize}
            prevToken={disputes?.meta?.prevToken}
            nextToken={disputes?.meta?.nextToken}
            resultCount={disputes?.meta?.resultCount}
            virtualPage={virtualPage}
            setVirtualPage={handleSetVirtualPage}
            dataSource={disputes?.results}
            // @ts-ignore
            columns={columns}
            onChange={handleTableChange}
          />
        </StyledCol>
      </Row>
      {isDisputeModalVisible && (
        <ManageDisputeModal
          dispute={activeRecord}
          title={`Dispute Account (ID: ${activeRecord?.id})`}
          open={isDisputeModalVisible}
          onOk={onManageDisputeModalCancel}
          onCancel={onManageDisputeModalCancel}
          debtorId={activeRecord?.debtorId}
        />
      )}
    </>
  );
}
